import { useCallback, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { chunk } from "@/lib/chunk";
import { isExcludedFromBilling as isExcluded, isPlannedEmpty } from "@/lib/billing-exclusion";

/** Keep `.in()` request URLs well under the length limit. */
const ID_CHUNK_SIZE = 150;
/** RPC ids travel in the POST body; same batch size as the CSV export. */
const RPC_CHUNK_SIZE = 500;

interface BillingEvidence {
  /** order_id -> planned `legs.is_loaded` for each leg. */
  legLoadsByOrder: Map<string, (boolean | null)[]>;
  /** order_id -> trailer load status at delivery. */
  loadStatusAtDelivery: Map<string, string>;
}

/**
 * Load evidence for the Unbillable dashboard, fetched only for the candidate
 * orders the caller passes (already missing a document), never tenant-wide.
 * Rule: `src/lib/billing-exclusion.ts`.
 *
 * Uses the per-order `order_load_status_at_delivery` RPC. The tenant-wide
 * `orders_load_status_at_delivery` it replaces scanned every delivered order,
 * hit the statement timeout, and silently fell back to live trailer state.
 * On failure here `error` is set and nothing is excluded except bobtails.
 */
export function useBillingExclusion(tenantId: string | null | undefined, orderIds: string[]) {
  const ids = useMemo(() => [...new Set(orderIds)].sort(), [orderIds]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["billing-exclusion-evidence", tenantId, ids],
    enabled: !!tenantId && ids.length > 0,
    queryFn: () => fetchBillingEvidence(tenantId!, ids),
    // A delivered order's load status at delivery doesn't change; avoid
    // re-running every batch on each focus/remount.
    staleTime: 5 * 60_000,
    // Keep the last evidence while a changed candidate set loads, so the
    // lists don't briefly re-include empty moves.
    placeholderData: keepPreviousData,
  });

  const isExcludedFromBilling = useCallback(
    (order: { id: string; trailer_type?: { kind?: string | null } | null }): boolean => {
      if (!data) return order.trailer_type?.kind === "bobtail";
      return isExcluded(
        order,
        data.legLoadsByOrder.get(order.id),
        data.loadStatusAtDelivery.get(order.id),
      );
    },
    [data],
  );

  return {
    isExcludedFromBilling,
    isLoading: ids.length > 0 && isLoading,
    error: error as Error | null,
  };
}

async function fetchBillingEvidence(tenantId: string, orderIds: string[]): Promise<BillingEvidence> {
  const legLoadsByOrder = new Map<string, (boolean | null)[]>();
  for (const part of chunk(orderIds, ID_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from("legs")
      .select("order_id, is_loaded")
      .eq("tenant_id", tenantId)
      .in("order_id", part);
    if (error) throw error;
    for (const l of (data ?? []) as { order_id: string; is_loaded: boolean | null }[]) {
      const list = legLoadsByOrder.get(l.order_id);
      if (list) list.push(l.is_loaded);
      else legLoadsByOrder.set(l.order_id, [l.is_loaded]);
    }
  }

  // Planned-empty orders are already settled; only the rest need history.
  const needHistory = orderIds.filter((id) => !isPlannedEmpty(legLoadsByOrder.get(id)));
  const loadStatusAtDelivery = new Map<string, string>();
  for (const part of chunk(needHistory, RPC_CHUNK_SIZE)) {
    const { data, error } = await supabase.rpc("order_load_status_at_delivery", {
      p_order_ids: part,
    });
    if (error) throw error;
    for (const r of (data ?? []) as { order_id: string; load_status: string | null }[]) {
      if (r.order_id && r.load_status) loadStatusAtDelivery.set(r.order_id, r.load_status);
    }
  }

  return { legLoadsByOrder, loadStatusAtDelivery };
}
